import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { BrandingProvider } from "@/lib/branding";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import NewOrder from "@/pages/NewOrder";
import Customers from "@/pages/Customers";
import Drivers from "@/pages/Drivers";
import Products from "@/pages/Products";
import Orders from "@/pages/OrderHistory";
import Administration from "@/pages/Administration";
import Fleet from "@/pages/Fleet";
import Tasks from "@/pages/Tasks";
import TaskSlaReport from "@/pages/TaskSlaReport";
import DispatchBoard from "@/pages/DispatchBoard";
import Workforce from "@/pages/Workforce";
import InventoryHealth from "@/pages/InventoryHealth";
import Purchasing from "@/pages/Purchasing";
import Pricing from "@/pages/Pricing";
import PriceNotifications from "@/pages/PriceNotifications";
import PriceNotificationDetail from "@/pages/PriceNotificationDetail";
import Receiving from "@/pages/Receiving";
import ReceivingDetail from "@/pages/ReceivingDetail";
import FreightExceptions from "@/pages/FreightExceptions";
import FreightOutstanding from "@/pages/FreightOutstanding";
import OperatorMetrics from "@/pages/OperatorMetrics";
import ParserHealth from "@/pages/ParserHealth";
import CommandPalette from "@/components/common/CommandPalette";
import DriverPriceNotice from "@/pages/DriverPriceNotice";
import StartTheDay from "@/pages/StartTheDay";
import StartTheDayCapture from "@/pages/StartTheDayCapture";
import SprintPlaceholder from "@/pages/SprintPlaceholder";
import Reconciliation from "@/pages/Reconciliation";
import DailyPackage from "@/pages/DailyPackage";
import ErrorBoundary from "@/components/ErrorBoundary";
import FifoIntelligence from "@/pages/FifoIntelligence";
import MorningBrief from "@/pages/MorningBrief";
import PhoneMessages from "@/pages/PhoneMessages";
import MorningCapture from "@/pages/MorningCapture";
import ThresholdAdministration from "@/pages/ThresholdAdministration";
import EmailAudit from "@/pages/EmailAudit";
import History from "@/pages/History";
import OperationsCenter from "@/pages/OperationsCenter";
import EmployeeProfile from "@/pages/EmployeeProfile";
import SignIn from "@/pages/SignIn";
import AccessDenied from "@/pages/AccessDenied";
import Diagnostics from "@/pages/Diagnostics";
import { getHomeRoute } from "@/lib/appConfig";

function Gate() {
  const { status, user } = useAuth();
  if (status === "loading") {
    return <div className="flex min-h-screen items-center justify-center text-gray-500">Signing in…</div>;
  }
  if (status === "signed_out") return <SignIn />;
  if (status === "denied") return <AccessDenied />;
  const homeRoute = getHomeRoute(user);
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Navigate to={homeRoute} replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/new-order" element={<NewOrder />} />
        <Route path="/todays-orders" element={<Orders />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/drivers" element={<Drivers />} />
        <Route path="/products" element={<Products />} />
        <Route path="/fleet" element={<Fleet />} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/tasks/report" element={<TaskSlaReport />} />
        <Route path="/administration" element={<Administration />} />
        <Route path="/workforce" element={<Workforce />} />
        <Route path="/workforce/employees/:id" element={<EmployeeProfile />} />
        <Route path="/inventory-health" element={<InventoryHealth />} />
        <Route path="/purchasing" element={<Purchasing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/start-the-day" element={<StartTheDay />} />
        <Route path="/start-the-day/daily-readings" element={<StartTheDayCapture initialSection="daily" />} />
        <Route path="/start-the-day/tank-inventory" element={<StartTheDayCapture initialSection="tank" />} />
        <Route
          path="/start-the-day/reconciliation"
          element={<Reconciliation />}
        />
        <Route path="/price-notifications" element={<PriceNotifications />} />
        <Route path="/price-notifications/:id" element={<PriceNotificationDetail />} />
        <Route path="/receiving" element={<Receiving />} />
        <Route path="/receiving/freight-exceptions" element={<FreightExceptions />} />
        <Route path="/receiving/freight-outstanding" element={<FreightOutstanding />} />
        <Route path="/receiving/:id" element={<ReceivingDetail />} />
        <Route path="/fifo" element={<FifoIntelligence />} />
        <Route path="/brief" element={<MorningBrief />} />
        <Route path="/morning-capture" element={<MorningCapture />} />
        <Route path="/admin/thresholds" element={<ThresholdAdministration />} />
        <Route path="/settings/email-audit" element={<EmailAudit />} />
        <Route path="/history" element={<History />} />
        <Route path="/ops" element={<OperationsCenter />} />
        <Route path="/messages" element={<PhoneMessages />} />
        <Route path="/ops-metrics" element={<OperatorMetrics />} />
        <Route path="/admin/parser-health" element={<ParserHealth />} />
        <Route path="/diagnostics" element={<Diagnostics />} />
      </Route>
      {/* Driver Price Notice — standalone, printable, shareable (no app chrome). */}
      <Route path="/driver-price-notice/:date" element={<DriverPriceNotice />} />
      {/* Daily Operating Package — standalone printable report (no sidebar). */}
      <Route path="/daily-package" element={<DailyPackage />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <BrandingProvider>
          <AuthProvider>
            <CommandPalette />
            <Gate />
          </AuthProvider>
        </BrandingProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
